import {
  parsePolicyLimitLabel,
  type ProfileInsuranceOverviewSourcePolicy,
} from "./profileInsuranceOverview";

const POLICY_LIMIT_PATTERN = /\$[\d,.]+(?:\s*[KMB])?/i;

export type ProfileInsuranceTypeOption = Readonly<{
  insuranceType: string;
  displayName: string;
  purpose: string;
}>;

/** Mirrors governed backend insurance types for owner editor UX. */
export const PROFILE_INSURANCE_TYPE_LIBRARY: readonly ProfileInsuranceTypeOption[] = [
  {
    insuranceType: "general_liability",
    displayName: "Liability Insurance",
    purpose: "Pays for damage or injuries you cause to others",
  },
  {
    insuranceType: "hull",
    displayName: "Hull Insurance",
    purpose: "Covers damage to your drone",
  },
  {
    insuranceType: "equipment",
    displayName: "Equipment Insurance",
    purpose: "Covers cameras, sensors, batteries, controllers, and laptops",
  },
  {
    insuranceType: "errors_and_omissions",
    displayName: "Errors & Omissions (Professional Liability)",
    purpose: "Covers mistakes in your work such as bad data or missed defects",
  },
  {
    insuranceType: "cyber_liability",
    displayName: "Cyber Liability",
    purpose: "Covers data breaches or hacked systems",
  },
  {
    insuranceType: "workers_comp",
    displayName: "Workers' Compensation",
    purpose: "Covers employees injured on the job",
  },
  {
    insuranceType: "commercial_auto",
    displayName: "Commercial Auto",
    purpose: "Covers vehicles transporting equipment",
  },
  {
    insuranceType: "general_business",
    displayName: "General Business Insurance",
    purpose: "Covers office, lawsuits, property, and related business risks",
  },
  {
    insuranceType: "aviation_liability",
    displayName: "Aviation Liability Insurance",
    purpose: "Aviation-specific liability coverage for drone operations",
  },
  {
    insuranceType: "umbrella",
    displayName: "Umbrella Insurance",
    purpose: "Extra liability coverage above primary policies",
  },
  {
    insuranceType: "other",
    displayName: "Other Insurance",
    purpose: "Other commercial insurance coverage",
  },
] as const;

const insuranceTypeLabelByKey = new Map(
  PROFILE_INSURANCE_TYPE_LIBRARY.map((option) => [option.insuranceType, option.displayName]),
);

export type ProfileInsuranceEditorEntry = Readonly<{
  entryKey: string;
  insuranceType: string;
  providerName: string;
  brokerEmail: string;
  policyLimit: string;
  insurancePolicyId?: string;
  coiDocumentId?: string;
  coiFileName?: string;
  coiFile?: File;
  recordStatus?: string;
  /** UI-only: verified policy marked for removal after disable step. */
  removalUnlocked?: boolean;
}>;

export type ProfileInsuranceEditorDraft = Readonly<{
  entries: readonly ProfileInsuranceEditorEntry[];
  pendingRevokePolicyIds?: readonly string[];
}>;

export type ProfileInsuranceEditorSeedSource = Readonly<{
  insuranceSummary?: string;
  insurancePolicies?: readonly ProfileInsuranceOverviewSourcePolicy[];
}>;

export type ProfileInsuranceEditorSyncPolicy = Readonly<{
  insurancePolicyId?: string;
  insuranceType: string;
  issuingInstitution: string;
  policyLimitSummary?: string;
  brokerEmail?: string;
}>;

let nextInsuranceEntryKey = 0;

export function createProfileInsuranceEditorEntryKey(): string {
  nextInsuranceEntryKey += 1;
  return `insurance-entry:${nextInsuranceEntryKey}`;
}

export function profileInsuranceTypeLabel(insuranceType: string): string {
  return insuranceTypeLabelByKey.get(insuranceType) ?? "Insurance coverage";
}

export function isProfileInsuranceEntryEditable(recordStatus?: string): boolean {
  if (!recordStatus) return true;
  return recordStatus === "draft" || recordStatus === "submitted";
}

export function isVerifiedInsuranceRecordStatus(recordStatus?: string): boolean {
  return recordStatus === "verified" || recordStatus === "pending_verification";
}

export function isExpiredInsuranceRecordStatus(recordStatus?: string): boolean {
  return recordStatus === "expired";
}

export function shouldShowDisableInsuranceControl(entry: ProfileInsuranceEditorEntry): boolean {
  return isVerifiedInsuranceRecordStatus(entry.recordStatus) && !entry.removalUnlocked;
}

export function shouldShowRemoveInsuranceControl(entry: ProfileInsuranceEditorEntry): boolean {
  if (!entry.recordStatus || isProfileInsuranceEntryEditable(entry.recordStatus)) {
    return true;
  }
  if (isExpiredInsuranceRecordStatus(entry.recordStatus)) {
    return true;
  }
  return isVerifiedInsuranceRecordStatus(entry.recordStatus) && entry.removalUnlocked === true;
}

/** @deprecated Use shouldShowRemoveInsuranceControl */
export function isProfileInsuranceEntryRemovable(recordStatus?: string): boolean {
  return isProfileInsuranceEntryEditable(recordStatus);
}

export function enableProfileInsuranceEditorEntryRemoval(
  draft: ProfileInsuranceEditorDraft,
  entryKey: string,
): ProfileInsuranceEditorDraft {
  return {
    ...draft,
    entries: draft.entries.map((entry) => (
      entry.entryKey === entryKey ? { ...entry, removalUnlocked: true } : entry
    )),
  };
}

export function buildInsuranceEditorRevokePolicyIds(
  draft: ProfileInsuranceEditorDraft,
): readonly string[] {
  return draft.pendingRevokePolicyIds ?? [];
}

export function isBlankInsuranceEditorEntry(
  entry: ProfileInsuranceEditorEntry,
): boolean {
  return !entry.providerName.trim()
    && !entry.policyLimit.trim()
    && !entry.coiFile
    && !entry.coiDocumentId;
}

export function listAvailableInsuranceTypes(
  draft: ProfileInsuranceEditorDraft,
): readonly ProfileInsuranceTypeOption[] {
  const usedTypes = new Set(draft.entries.map((entry) => entry.insuranceType));
  return PROFILE_INSURANCE_TYPE_LIBRARY.filter(
    (option) => !usedTypes.has(option.insuranceType),
  );
}

export function listProfileInsuranceTypePickerOptions(
  draft: ProfileInsuranceEditorDraft,
  entryKey: string,
): readonly ProfileInsuranceTypeOption[] {
  const usedTypes = new Set(
    draft.entries
      .filter((entry) => entry.entryKey !== entryKey)
      .map((entry) => entry.insuranceType),
  );
  return PROFILE_INSURANCE_TYPE_LIBRARY.filter(
    (option) => !usedTypes.has(option.insuranceType),
  );
}

function providerFromSummary(insuranceSummary: string | undefined): string | undefined {
  const normalized = insuranceSummary?.trim();
  if (!normalized) return undefined;

  const segments = normalized.split(/\s+-\s+/);
  const trailing = segments[segments.length - 1]?.trim();
  if (segments.length > 1 && trailing && !POLICY_LIMIT_PATTERN.test(trailing)) {
    return trailing;
  }
  return undefined;
}

export function formatInsuranceSummaryPolicyLimit(policyLimit: string): string {
  const trimmed = policyLimit.trim();
  if (!trimmed) return "";

  if (/coverage/i.test(trimmed)) {
    return trimmed;
  }

  const amountMatch = trimmed.match(POLICY_LIMIT_PATTERN);
  if (amountMatch) {
    const amount = amountMatch[0].replace(/\s+/g, "");
    return `${amount} Coverage`;
  }

  return trimmed;
}

function createEmptyEntry(insuranceType = "general_liability"): ProfileInsuranceEditorEntry {
  return {
    entryKey: createProfileInsuranceEditorEntryKey(),
    insuranceType,
    providerName: "",
    brokerEmail: "",
    policyLimit: "",
  };
}

function isValidBrokerEmail(value: string | undefined): boolean {
  const normalized = value?.trim() ?? "";
  if (!normalized) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

function mapPolicyToEntry(
  policy: ProfileInsuranceOverviewSourcePolicy,
  fallbackPolicyLimit?: string,
): ProfileInsuranceEditorEntry {
  return {
    entryKey: createProfileInsuranceEditorEntryKey(),
    insuranceType: policy.insuranceType,
    providerName: policy.issuingInstitution.trim(),
    policyLimit: policy.policyLimitSummary?.trim()
      || (fallbackPolicyLimit && fallbackPolicyLimit !== "Not listed" ? fallbackPolicyLimit : ""),
    brokerEmail: policy.brokerEmail?.trim() ?? "",
    ...(policy.id ? { insurancePolicyId: policy.id } : {}),
    ...(policy.coiDocumentId ? { coiDocumentId: policy.coiDocumentId } : {}),
    recordStatus: policy.recordStatus,
  };
}

export function seedProfileInsuranceEditorDraft(
  source: ProfileInsuranceEditorSeedSource,
): ProfileInsuranceEditorDraft {
  const policies = source.insurancePolicies ?? [];
  const fallbackPolicyLimit = parsePolicyLimitLabel(source.insuranceSummary);

  if (policies.length === 0) {
    const providerName = providerFromSummary(source.insuranceSummary);
    if (providerName || fallbackPolicyLimit) {
      return {
        entries: [{
          ...createEmptyEntry("general_liability"),
          providerName: providerName ?? "",
          policyLimit: fallbackPolicyLimit && fallbackPolicyLimit !== "Not listed"
            ? fallbackPolicyLimit
            : "",
        }],
      };
    }
    return { entries: [] };
  }

  return {
    entries: policies.map((policy) => mapPolicyToEntry(policy, fallbackPolicyLimit)),
  };
}

export function validateProfileInsuranceEditorDraft(
  draft: ProfileInsuranceEditorDraft,
): string | null {
  const substantiveEntries = draft.entries.filter(
    (entry) => isProfileInsuranceEntryEditable(entry.recordStatus)
      && !isBlankInsuranceEditorEntry(entry),
  );

  for (const entry of substantiveEntries) {
    if (!entry.providerName.trim()) {
      return `${profileInsuranceTypeLabel(entry.insuranceType)}: provider name is required.`;
    }
    if (!entry.policyLimit.trim()) {
      return `${profileInsuranceTypeLabel(entry.insuranceType)}: policy limit is required.`;
    }
    if (!isValidBrokerEmail(entry.brokerEmail)) {
      return `${profileInsuranceTypeLabel(entry.insuranceType)}: broker email is invalid.`;
    }
  }

  const seenTypes = new Set<string>();
  for (const entry of substantiveEntries) {
    if (seenTypes.has(entry.insuranceType)) {
      return "Each insurance type can only be added once.";
    }
    seenTypes.add(entry.insuranceType);
  }

  return null;
}

export function buildInsuranceEditorSyncPolicies(
  draft: ProfileInsuranceEditorDraft,
): readonly ProfileInsuranceEditorSyncPolicy[] {
  return draft.entries
    .filter((entry) => isProfileInsuranceEntryEditable(entry.recordStatus)
      && !isBlankInsuranceEditorEntry(entry))
    .map((entry) => ({
      ...(entry.insurancePolicyId ? { insurancePolicyId: entry.insurancePolicyId } : {}),
      insuranceType: entry.insuranceType,
      issuingInstitution: entry.providerName.trim(),
      policyLimitSummary: formatInsuranceSummaryPolicyLimit(entry.policyLimit) || undefined,
      ...(entry.brokerEmail?.trim()
        ? { brokerEmail: entry.brokerEmail.trim().toLowerCase() }
        : {}),
    }));
}

export function buildInsuranceOverviewSeedFromDraft(
  draft: ProfileInsuranceEditorDraft,
): ProfileInsuranceEditorSeedSource {
  const substantiveEntries = draft.entries.filter(
    (entry) => isProfileInsuranceEntryEditable(entry.recordStatus)
      && !isBlankInsuranceEditorEntry(entry),
  );
  const verifiedEntries = draft.entries.filter((entry) => !isProfileInsuranceEntryEditable(entry.recordStatus));
  const primaryLimit = substantiveEntries[0]?.policyLimit ?? verifiedEntries[0]?.policyLimit;
  const visibleEntries = draft.entries.filter((entry) => !isBlankInsuranceEditorEntry(entry));

  return {
    insuranceSummary: primaryLimit
      ? formatInsuranceSummaryPolicyLimit(primaryLimit)
      : undefined,
    insurancePolicies: visibleEntries.map((entry) => ({
      id: entry.insurancePolicyId ?? `draft-insurance:${entry.entryKey}`,
      insuranceType: entry.insuranceType,
      issuingInstitution: entry.providerName.trim(),
      recordStatus: entry.recordStatus ?? "submitted",
      ...(formatInsuranceSummaryPolicyLimit(entry.policyLimit)
        ? { policyLimitSummary: formatInsuranceSummaryPolicyLimit(entry.policyLimit) }
        : {}),
      ...(entry.coiDocumentId ? { coiDocumentId: entry.coiDocumentId } : {}),
    })),
  };
}

export function addProfileInsuranceEditorEntry(
  draft: ProfileInsuranceEditorDraft,
): ProfileInsuranceEditorDraft {
  const nextType = listAvailableInsuranceTypes(draft)[0]?.insuranceType;
  if (!nextType) {
    return draft;
  }

  return {
    entries: [...draft.entries, createEmptyEntry(nextType)],
  };
}

export function removeProfileInsuranceEditorEntry(
  draft: ProfileInsuranceEditorDraft,
  entryKey: string,
): ProfileInsuranceEditorDraft {
  const removed = draft.entries.find((entry) => entry.entryKey === entryKey);
  const pendingRevokePolicyIds = [...(draft.pendingRevokePolicyIds ?? [])];
  if (
    removed?.insurancePolicyId
    && (
      isExpiredInsuranceRecordStatus(removed.recordStatus)
      || (isVerifiedInsuranceRecordStatus(removed.recordStatus) && removed.removalUnlocked)
    )
  ) {
    pendingRevokePolicyIds.push(removed.insurancePolicyId);
  }

  return {
    entries: draft.entries.filter((entry) => entry.entryKey !== entryKey),
    ...(pendingRevokePolicyIds.length > 0 ? { pendingRevokePolicyIds } : {}),
  };
}
